import { type Dispatch, type SetStateAction, useContext } from 'react'

import BrokerContext from 'components/context/BrokerContext'
import NeedsAttentionContext from 'components/context/NeedsAttentionContext'
import { type NAVv2Filters } from 'components/hooks/useGetBrokersMissingPayments'
import { Button } from 'slab'
import { type BrokersBroker } from 'utils/web_api.schemas'

type NeedsAttentionQuickFiltersProps = {
  filters: NAVv2Filters
  setFilters: Dispatch<SetStateAction<NAVv2Filters>>
}

// The 5 queries below mimic the Ops NAV dashboard queries for certain sets of issues. These filters
// overwrite the existing query entirely.
function setHighPriorityFilters (
  setFilters: Dispatch<SetStateAction<NAVv2Filters>>
) {
  setFilters(() => ({
    statuses: { include: ['active'] },
    reasons: {
      include: [
        'delinquency_risk',
        'under_payment',
        'unexpected_amount',
        'automation_reconciliation',
      ],
    },
    employment_statuses: { include: ['active'] },
    enrollment_categories: { include: ['general_medical'] },
    submission_methods: {
      exclude: [
        'off_thatch',
        'vsp_browser_automation',
        'renaissance_browser_automation',
        'guardian_browser_automation',
      ],
    },
  }))
}

function setAncillariesFilters (
  setFilters: Dispatch<SetStateAction<NAVv2Filters>>
) {
  setFilters(() => ({
    statuses: { include: ['active'] },
    enrollment_types: { include: ['dental', 'vision'] },
    employment_statuses: { include: ['active'] },
    enrollment_categories: {
      include: ['guardian_dental', 'renaissance_dental', 'vsp', 'bsca_dental', 'bsca_vision'],
    },
  }))
}

function setPaymentDiscrepanciesFilters (
  setFilters: Dispatch<SetStateAction<NAVv2Filters>>
) {
  setFilters(() => ({
    statuses: { include: ['active'] },
    reasons: {
      include: ['over_payment', 'missing_enrollment'],
    },
    employment_statuses: { include: ['active'] },
    enrollment_categories: { include: ['general_medical'] },
    submission_methods: {
      exclude: [
        'off_thatch',
        'vsp_browser_automation',
        'renaissance_browser_automation',
        'guardian_browser_automation',
      ],
    },
  }))
}

function setPartnerIssuesFilters (
  setFilters: Dispatch<SetStateAction<NAVv2Filters>>,
  currentBroker: BrokersBroker | undefined
) {
  setFilters(() => ({
    statuses: { include: ['active'] },
    employment_statuses: { include: ['active'] },
    enrollment_categories: { include: ['general_medical'] },
    submission_methods: {
      exclude: ['manual', 'auto_renewal', 'member_plan_change_with_carrier'],
    },
    ...(currentBroker
      ? { brokerage_ids: { exclude: [currentBroker?.brokerage_id] } }
      : {}),
  }))
}

function setOffboardingIssuesFilters (
  setFilters: Dispatch<SetStateAction<NAVv2Filters>>
) {
  setFilters(() => ({
    statuses: { include: ['active'] },
    employment_statuses: { include: ['pending', 'inactive'] },
    submission_methods: {
      exclude: ['off_thatch'],
    },
  }))
}

function setAutomationIssuesFilters (
  setFilters: Dispatch<SetStateAction<NAVv2Filters>>
) {
  setFilters(() => ({
    statuses: { include: ['active'] },
    reasons: {
      include: ['automation_reconciliation'],
    },
  }))
}

function setManualIssuesFilters (
  setFilters: Dispatch<SetStateAction<NAVv2Filters>>
) {
  setFilters(() => ({
    statuses: { include: ['active'] },
    reasons: {
      include: [
        'incorrect_configuration',
        'payments_issue',
        'backdate_requested',
        'other',
      ],
    },
  }))
}
// These queries below are for the filters that are not included in the Ops NAV dashboard queries.
// They do not overwrite the existing query, but rather add to it to augment the existing query for better triaging.

function setUnresolvedFilters (
  setFilters: Dispatch<SetStateAction<NAVv2Filters>>
) {
  setFilters((prev) => ({
    ...prev,
    statuses: { include: ['active', 'silenced'] },
  }))
}

function setAssignedToMeFilters (
  setFilters: Dispatch<SetStateAction<NAVv2Filters>>,
  currentBroker: BrokersBroker | undefined
) {
  setFilters((prev) => ({
    ...prev,
    ...(currentBroker ? { broker_ids: { include: [currentBroker?.id] } } : {}),
  }))
}

function setUnassignedFilters (
  setFilters: Dispatch<SetStateAction<NAVv2Filters>>
) {
  setFilters((prev) => ({
    ...prev,
    broker_ids: { include: ['unassigned'] },
  }))
}

function setVipEmployerFilters (
  setFilters: Dispatch<SetStateAction<NAVv2Filters>>
) {
  setFilters((prev) => ({
    ...prev,
    vip_employer: 'true',
  }))
}

const NeedsAttentionQuickFilters = ({
  setFilters,
}: NeedsAttentionQuickFiltersProps) => {
  const { isNAVv2ToolingEnabled } = useContext(NeedsAttentionContext)
  const { currentBroker } = useContext(BrokerContext)

  if (!isNAVv2ToolingEnabled) {
    return null
  }

  return (
    <div className='ml-1 flex items-center justify-start gap-2 flex-shrink-0 flex-wrap'>
      <div className='text-sm font-semibold min-w-16'>Quick filters</div>
      <Button
        variant='tertiary'
        onClick={() => setHighPriorityFilters(setFilters)}
      >
        High Priority
      </Button>
      <Button
        variant='tertiary'
        onClick={() => setAncillariesFilters(setFilters)}
      >
        Ancillaries
      </Button>
      <Button
        variant='tertiary'
        onClick={() => setPaymentDiscrepanciesFilters(setFilters)}
      >
        Payment Discrepancies
      </Button>
      <Button
        variant='tertiary'
        onClick={() => setPartnerIssuesFilters(setFilters, currentBroker)}
      >
        Partner Issues
      </Button>
      <Button
        variant='tertiary'
        onClick={() => setOffboardingIssuesFilters(setFilters)}
      >
        Offboarding Issues
      </Button>
      <Button
        variant='tertiary'
        onClick={() => setAutomationIssuesFilters(setFilters)}
      >
        Automation
      </Button>
      <Button
        variant='tertiary'
        onClick={() => setManualIssuesFilters(setFilters)}
      >
        Manual
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setUnresolvedFilters(setFilters)}
      >
        Unresolved
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setAssignedToMeFilters(setFilters, currentBroker)}
      >
        Assigned to me
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setUnassignedFilters(setFilters)}
      >
        Unassigned
      </Button>
      {isNAVv2ToolingEnabled && (
        <Button
          variant='tertiary-deemphasized'
          onClick={() => setVipEmployerFilters(setFilters)}
        >
          VIP Employers
        </Button>
      )}
    </div>
  )
}

export default NeedsAttentionQuickFilters
